import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import NuevoTurnoModal from "@/components/NuevoTurnoModal";
import {
  ArrowLeft, Phone, Mail, MapPin, Shield, CreditCard, Plus, Pencil, Trash2, User,
} from "lucide-react";
import { toast } from "sonner";

const API = "/api";

const EstadoBadge = ({ estado }) => {
  const cls = {
    pendiente: "badge-pendiente",
    confirmado: "badge-confirmado",
    completado: "badge-completado",
    cancelado: "badge-cancelado",
  };
  return <span className={cls[estado] || "badge-pendiente"}>{estado || "pendiente"}</span>;
};

export default function PacientePage() {
  const { dni } = useParams();
  const navigate = useNavigate();
  const { getAuthHeader } = useAuth();
  const [turnos, setTurnos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editTurno, setEditTurno] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/pacientes/${dni}/turnos`, { headers: getAuthHeader() });
      setTurnos(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [dni]);

  const paciente = turnos[0] || null;

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar este turno?")) return;
    try {
      await axios.delete(`${API}/turnos/${id}`, { headers: getAuthHeader() });
      toast.success("Turno eliminado");
      load();
    } catch {
      toast.error("Error al eliminar");
    }
  };

  return (
    <div className="p-4 md:p-8">
      {/* Back */}
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 mb-6 transition-colors"
        data-testid="back-btn"
      >
        <ArrowLeft size={16} />
        Volver
      </button>

      {loading ? (
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-slate-100 rounded w-48" />
          <div className="h-4 bg-slate-100 rounded w-64" />
        </div>
      ) : !paciente ? (
        <div className="text-center py-20">
          <User size={40} className="text-slate-200 mx-auto mb-3" />
          <p className="text-slate-400">Paciente no encontrado</p>
        </div>
      ) : (
        <>
          {/* Patient header */}
          <div className="bg-white border border-slate-100 rounded-2xl p-6 mb-6" data-testid="paciente-info">
            <div className="flex flex-col sm:flex-row items-start justify-between gap-4">
              <div className="flex items-center gap-5">
                <div className="w-16 h-16 rounded-2xl bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-blue-700 font-bold text-2xl">
                    {(paciente.nombre?.[0] || "?").toUpperCase()}
                  </span>
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-slate-900">
                    {paciente.nombre} {paciente.apellido}
                  </h1>
                  <p className="text-slate-400 text-sm mt-0.5">DNI: {paciente.dni || "No registrado"}</p>
                  <p className="text-xs text-slate-400 mt-1">{turnos.length} turno{turnos.length !== 1 ? "s" : ""} en total</p>
                </div>
              </div>

              <button
                onClick={() => { setEditTurno(null); setShowModal(true); }}
                className="btn-primary"
                data-testid="nuevo-turno-paciente-btn"
              >
                <Plus size={16} />
                Nuevo turno
              </button>
            </div>

            {/* Info grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 mt-6 pt-5 border-t border-slate-50">
              {paciente.telefono && (
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <Phone size={14} className="text-slate-400 flex-shrink-0" />
                  {paciente.telefono}
                </div>
              )}
              {paciente.email && (
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <Mail size={14} className="text-slate-400 flex-shrink-0" />
                  <span className="truncate">{paciente.email}</span>
                </div>
              )}
              {paciente.cobertura && (
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <Shield size={14} className="text-slate-400 flex-shrink-0" />
                  {paciente.cobertura}
                </div>
              )}
              {paciente.credencial && (
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <CreditCard size={14} className="text-slate-400 flex-shrink-0" />
                  {paciente.credencial}
                </div>
              )}
              {paciente.direccion && (
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <MapPin size={14} className="text-slate-400 flex-shrink-0" />
                  {paciente.direccion}{paciente.cp ? `, CP ${paciente.cp}` : ""}
                </div>
              )}
            </div>
          </div>

          {/* Historial */}
          <div className="bg-white border border-slate-100 rounded-2xl overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-50">
              <h3 className="font-bold text-slate-900">Historial de turnos</h3>
            </div>

            {turnos.length === 0 ? (
              <div className="text-center py-12 text-slate-400 text-sm">Sin turnos registrados</div>
            ) : (
              <div className="divide-y divide-slate-50">
                {turnos.map((t) => (
                  <div key={t.id} className="px-6 py-4 hover:bg-slate-50 transition-colors" data-testid="historial-turno-row">
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 flex-wrap">
                          <span className="font-semibold text-slate-900 text-sm">
                            {t.fecha_turno || "Sin fecha"} {t.hora_turno && `· ${t.hora_turno}`}
                          </span>
                          <EstadoBadge estado={t.estado} />
                          {t.pago_completado && (
                            <span className="badge-completado">
                              Pagado{t.monto_pago ? ` $${t.monto_pago}` : ""}
                            </span>
                          )}
                        </div>
                        <div className="flex gap-4 mt-1.5 flex-wrap">
                          {t.servicio && (
                            <span className="text-xs text-slate-500">{t.servicio}</span>
                          )}
                          {t.cobertura && (
                            <span className="text-xs text-slate-400">{t.cobertura}</span>
                          )}
                        </div>
                        {(t.direccion || t.cp) && (
                          <p className="text-xs text-slate-400 mt-1">{t.direccion}{t.cp ? `, CP ${t.cp}` : ""}</p>
                        )}
                      </div>

                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        <button
                          onClick={() => { setEditTurno(t); setShowModal(true); }}
                          className="p-1.5 rounded-lg hover:bg-blue-50 text-slate-400 hover:text-blue-600 transition-colors"
                          data-testid="edit-historial-btn"
                        >
                          <Pencil size={14} />
                        </button>
                        <button
                          onClick={() => handleDelete(t.id)}
                          className="p-1.5 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-600 transition-colors"
                          data-testid="delete-historial-btn"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      <NuevoTurnoModal
        isOpen={showModal}
        onClose={() => { setShowModal(false); setEditTurno(null); }}
        onSuccess={load}
        turnoEdit={editTurno}
      />
    </div>
  );
}
